import {
  Box,
  OptionPanelActionRow,
  OptionPanelRow,
  OptionPanelSection,
  OptionPanelSeparator,
  Switch,
} from "@salestrak/ui";

import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { useTrackViewOption, useViews } from "#app/lib/views/view-context";
import { DisabledReason, NO_PERMISSION } from "#app/components/menus/disabled-reason";

/**
 * The panel's More options page: the rarer display toggle off the main
 * page's Display group, the view's own settings (how it opens for a
 * reader), and the reset action. Everything here edits the DRAFT config like
 * the main page's toggles — the view previews it and the toolbar's Save
 * commits it, so a reset is revertable with Discard until then — and so
 * every row is open to every viewer; without `create.views` the edit is
 * temporary. The one exception is the Only me default: it decides how the
 * view opens for EVERY reader, a shared-row setting in draft clothing, so
 * it locks behind `create.views` and says why.
 */
export function ViewOptionsMore() {
  const { config, setConfig, canManage, defaultConfig } = useViews();
  const trackOption = useTrackViewOption();
  const toggle = (key: "onlyMe" | "showCompleted" | "showCanceled" | "showArchived", on: boolean) => {
    setConfig({ [key]: on });
    trackOption(key, { on });
  };

  const onlyMeRow = (
    <OptionPanelRow
      label="Default to Only Me"
      icon={ENTITY_KIND_ICON.user}
      disabled={!canManage}
    >
      <Switch
        size="sm"
        checked={config.onlyMe}
        disabled={!canManage}
        onCheckedChange={(checked) => toggle("onlyMe", !!checked)}
      />
    </OptionPanelRow>
  );

  return (
    <Box direction="column">
      {/* Which closed projects the view keeps. Completed and cancelled are
          shown by default and the switches narrow the view (`viewStageWhere`
          names the stages it keeps); archived are hidden by default and the
          switch widens it to include them. */}
      <OptionPanelSection title="Project visibility">
        <OptionPanelRow label="Show completed Projects">
          <Switch
            size="sm"
            checked={config.showCompleted}
            onCheckedChange={(checked) =>
              toggle("showCompleted", !!checked)
            }
          />
        </OptionPanelRow>
        <OptionPanelRow label="Show cancelled Projects">
          <Switch
            size="sm"
            checked={config.showCanceled}
            onCheckedChange={(checked) =>
              toggle("showCanceled", !!checked)
            }
          />
        </OptionPanelRow>
        <OptionPanelRow label="Show archived Projects">
          <Switch
            size="sm"
            checked={config.showArchived}
            onCheckedChange={(checked) =>
              toggle("showArchived", !!checked)
            }
          />
        </OptionPanelRow>
      </OptionPanelSection>

      <OptionPanelSeparator />

      {/* How the view opens for a reader, as opposed to what it shows: the
          Only me filter's saved position is a default the toolbar toggle
          can override for the session. */}
      <OptionPanelSection title="View settings">
        {canManage ? (
          onlyMeRow
        ) : (
          // The wrapper is the tooltip's trigger, so the row drops its
          // pointer events — a disabled switch would swallow the hover.
          <DisabledReason reason={NO_PERMISSION}>
            <Box fill="width" pointerEvents="none">
              {onlyMeRow}
            </Box>
          </DisabledReason>
        )}
      </OptionPanelSection>

      <OptionPanelSeparator />

      {/* Resets every display option to the type defaults — a draft edit like
          any other, so Save is what makes it stick and Discard undoes it. */}
      <OptionPanelSection>
        <OptionPanelActionRow
          label="Reset view to defaults"
          icon="undo"
          onClick={() => {
            setConfig(defaultConfig);
            trackOption("resetDefaults");
          }}
        />
      </OptionPanelSection>
    </Box>
  );
}
